import { Check, ChevronDown, CircleX, Eraser, ReplaceAll, Search, Shuffle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MARD_291, filterPaletteByCode } from '../core/palette';
import { MIXED_COLOR, getPatternReplaceCount, type PatternColorChoice, type PatternColorTarget, type PatternReplaceScope } from '../core/replacePatternColor';
import type { CanvasSelection, PatternProject } from '../types';

interface Props {
  project: PatternProject;
  selectedColor: number | null;
  selection: CanvasSelection | null;
  onClose: () => void;
  onConfirm: (source: PatternColorChoice, target: PatternColorTarget, scope: PatternReplaceScope, selection: CanvasSelection | null) => void;
}

interface ColorItem {
  value: PatternColorTarget;
  count?: number;
}

interface ColorDropdownProps {
  id: string;
  label: string;
  value: PatternColorTarget;
  items: ColorItem[];
  autoFocus?: boolean;
  onChange: (value: PatternColorTarget) => void;
}

function choiceLabel(value: PatternColorTarget) {
  return value === MIXED_COLOR ? '混色' : value === null ? '空白' : MARD_291[value]?.code || '?';
}

function choiceDescription(value: PatternColorTarget) {
  return value === MIXED_COLOR ? 'A1-M15 逐颗随机' : value === null ? '无豆子的格子' : 'MARD 色号';
}

function ChoiceSwatch({ value }: { value: PatternColorTarget }) {
  return <i className={`replace-color-swatch${value === null ? ' blank' : value === MIXED_COLOR ? ' mixed' : ''}`} style={value === null || value === MIXED_COLOR ? undefined : { background: MARD_291[value]?.hex }}>
    {value === null && <Eraser size={15} aria-hidden="true" />}
    {value === MIXED_COLOR && <Shuffle size={15} aria-hidden="true" />}
  </i>;
}

function ColorDropdown({ id, label, value, items, autoFocus, onChange }: ColorDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedItem = items.find((item) => item.value === value);
  const filtered = useMemo(() => {
    const keyword = query.trim();
    if (!keyword) return items;
    const mixed = items.filter((item) => item.value === MIXED_COLOR && '混色 RANDOM MIX A1M15'.includes(keyword.toUpperCase()));
    const blank = items.filter((item) => item.value === null && '空白 BLANK EMPTY 删除'.includes(keyword.toUpperCase()));
    const codeQuery = keyword.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const colors = codeQuery
      ? filterPaletteByCode(items.filter((item): item is ColorItem & { value: number } => typeof item.value === 'number'), codeQuery, (item) => MARD_291[item.value]?.code || '')
      : [];
    return [...mixed, ...blank, ...colors];
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => { document.removeEventListener('pointerdown', closeOutside); cancelAnimationFrame(frame); };
  }, [open]);

  return <div className="replace-color-picker" ref={rootRef} onKeyDown={(event) => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
    }
  }}>
    <button ref={buttonRef} id={id} className="replace-color-trigger" type="button" autoFocus={autoFocus} aria-haspopup="listbox" aria-expanded={open} aria-controls={`${id}-list`} onClick={() => { setQuery(''); setOpen((current) => !current); }}>
      <ChoiceSwatch value={value} />
      <span><strong>{choiceLabel(value)}</strong><small>{choiceDescription(value)}{selectedItem?.count === undefined ? '' : ` · ${selectedItem.count} ${value === null ? '格' : '颗'}`}</small></span>
      <ChevronDown size={16} />
    </button>
    {open && <div className="replace-color-popover">
      <label className="replace-color-search"><Search size={15} /><input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value.replace(/[^a-z0-9\u4e00-\u9fa5]/gi, ''))} placeholder="输入 MARD 色号，例如 A5" aria-label={`搜索${label}`} /></label>
      <div id={`${id}-list`} className="replace-color-list" role="listbox" aria-label={label}>
        {filtered.map((item) => {
          return <button key={item.value === null ? 'blank' : item.value} type="button" role="option" aria-selected={item.value === value} onClick={() => { onChange(item.value); setOpen(false); buttonRef.current?.focus(); }}>
            <ChoiceSwatch value={item.value} />
            <span><strong>{choiceLabel(item.value)}</strong><small>{choiceDescription(item.value)}</small></span>
            {item.count !== undefined && <em>{item.count} {item.value === null ? '格' : '颗'}</em>}
            {item.value === value && <Check size={15} />}
          </button>;
        })}
        {!filtered.length && <p>没有匹配的色号</p>}
      </div>
    </div>}
  </div>;
}

export function ColorReplaceModal({ project, selectedColor, selection, onClose, onConfirm }: Props) {
  const [lockedSelection] = useState<CanvasSelection | null>(() => selection ? { ...selection } : null);
  const usedColors = useMemo(() => {
    const counts = new Map<number, number>();
    for (let index = 0; index < project.colorIndices.length; index += 1) if (!project.empty[index]) counts.set(project.colorIndices[index], (counts.get(project.colorIndices[index]) || 0) + 1);
    return [...counts.entries()].sort((a, b) => (MARD_291[a[0]]?.code || '').localeCompare(MARD_291[b[0]]?.code || '', undefined, { numeric: true }));
  }, [project]);
  const blankCount = useMemo(() => project.empty.reduce((count, value) => count + (value ? 1 : 0), 0), [project]);
  const initialSource: PatternColorChoice = selectedColor !== null && usedColors.some(([index]) => index === selectedColor) ? selectedColor : usedColors[0]?.[0] ?? null;
  const [source, setSource] = useState<PatternColorChoice>(initialSource);
  const [target, setTarget] = useState<PatternColorTarget>(selectedColor !== null && selectedColor !== initialSource ? selectedColor : initialSource === 0 ? 1 : 0);
  const [scope, setScope] = useState<PatternReplaceScope>(lockedSelection ? 'selection' : 'all');
  const selectedCells = lockedSelection ? (Math.abs(lockedSelection.endCol - lockedSelection.startCol) + 1) * (Math.abs(lockedSelection.endRow - lockedSelection.startRow) + 1) : 0;
  const replaceCount = useMemo(() => getPatternReplaceCount(project, source, scope, lockedSelection), [lockedSelection, project, scope, source]);

  const sourceItems = useMemo<ColorItem[]>(() => [{ value: null, count: blankCount }, ...usedColors.map(([index, count]) => ({ value: index, count }))], [blankCount, usedColors]);
  const targetItems = useMemo<ColorItem[]>(() => [{ value: MIXED_COLOR }, { value: null }, ...MARD_291.map((_color, index) => ({ value: index }))], []);
  const sourceLabel = choiceLabel(source);
  const targetLabel = choiceLabel(target);
  const destructive = target === null && source !== null;
  const mixed = target === MIXED_COLOR;
  return <div className="modal-backdrop editor-dialog-layer" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="editor-dialog replace-dialog" role="dialog" aria-modal="true" aria-labelledby="replace-color-title">
      <button className="editor-dialog-close" type="button" onClick={onClose} aria-label="关闭批量换色"><CircleX size={20} /></button>
      <div className="editor-dialog-heading"><span className="editor-dialog-icon violet"><ReplaceAll size={21} /></span><div><p className="eyebrow">BATCH COLOR</p><h2 id="replace-color-title">批量更换颜色</h2><p>原色或替换色都可选择空白，用于批量补豆或删除多余颜色。</p></div></div>
      <div className="replace-color-flow">
        <label><span>原色</span><ColorDropdown id="replace-source" label="原色色卡" value={source} items={sourceItems} autoFocus onChange={(value) => { if (value !== MIXED_COLOR) setSource(value); }} /></label>
        <strong>→</strong>
        <label><span>替换为</span><ColorDropdown id="replace-target" label="目标色色卡" value={target} items={targetItems} onChange={setTarget} /></label>
      </div>
      <fieldset className="replace-scope"><legend>替换范围</legend><label><input type="radio" name="replace-scope" checked={scope === 'all'} onChange={() => setScope('all')} /><span><strong>整个画布</strong><small>{project.width * project.height} 格范围</small></span></label><label className={lockedSelection ? '' : 'disabled'}><input type="radio" name="replace-scope" checked={scope === 'selection'} disabled={!lockedSelection} onChange={() => setScope('selection')} /><span><strong>当前框选</strong><small>{lockedSelection ? `${selectedCells} 格范围（已锁定）` : '请先用框选工具选择区域'}</small></span></label></fieldset>
      <div className={`replace-preview${destructive ? ' delete' : mixed ? ' mixed' : ''}`}><ChoiceSwatch value={mixed ? MIXED_COLOR : source} /><span><strong>{mixed ? `将随机混色 ${replaceCount} ${source === null ? '格' : '颗'}` : source === null ? `将填充 ${replaceCount} 个空白格` : destructive ? `将删除 ${replaceCount} 颗豆子` : `将替换 ${replaceCount} 颗`}</strong><small>{sourceLabel} → {targetLabel}{destructive ? '，对应格将变为空白' : mixed ? '（A1-M15，每颗独立随机）' : ''}</small></span></div>
      <footer className="editor-dialog-actions"><button className="soft-dialog-button" type="button" onClick={onClose}>取消</button><button className={destructive ? 'danger-dialog-button' : 'primary-button compact'} type="button" disabled={source === target || replaceCount === 0} onClick={() => onConfirm(source, target, scope, lockedSelection)}>{destructive ? '确认删除' : mixed ? '确认混色' : source === null ? '确认填充' : '确认替换'}</button></footer>
    </section>
  </div>;
}
